import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';

// NGUPGRADE: Keep this in sync with intersectionObserver.ts
/**
 * This directive is used to delay the rendering of an element until it scrolls into view.
 *
 * The "onIntersection" output binding is triggered only when the directive element is visible in the
 * browser viewport (or the optional scrollable host container).
 *
 * @example
 *   <div
 *     dgxIntersectionObserver
 *     (onIntersection)="loadLotsOfThings()>
 *       <li *ngF="let thing of things">{{thing.property}}</li>
 *   </div>"
 */
@Directive({
  selector: '[dgxIntersectionObserver]',
})
export class IntersectionObserverDirective implements OnDestroy, AfterViewInit {
  @Input() public options: IntersectionObserverInit = {};
  @Input() public host: ElementRef<HTMLElement> = null;
  // When set to true it will unobserve the element after it comes into viewing area
  // When false it will allow element to continue to be observed in viewing area
  @Input() public unObserve: boolean = true;
  // Observe the default partial view port area below by setting to true
  // or observe the full view port when false
  @Input() public useDefaultViewArea = false;
  // Set to dynamically disable intersection observer
  @Input() public disable = false;
  // The "onIntersection" output returns the element to allow
  // mutation such as setting the src on an <img> etc.
  @Output()
  public onIntersection: EventEmitter<HTMLElement> = new EventEmitter();

  public defaultViewingArea: string = '-30% 0px -50% 0px';
  private observer: IntersectionObserver;

  constructor(private _element: ElementRef) {}

  public ngAfterViewInit(): void {
    if (this.disable) {
      return;
    }
    const options = {
      root: this.isHostScrollable() ? this.host.nativeElement : null,
      rootMargin: this.useDefaultViewArea ? this.defaultViewingArea : '0px',
      ...this.options,
    };

    this.observer = new IntersectionObserver(([entry], self) => {
      if (entry.isIntersecting) {
        this.onIntersection.emit(this._element.nativeElement);
        if (this.unObserve) {
          this.observer.unobserve(this._element.nativeElement);
        }
      }
    }, options);
    this.observer.observe(this._element.nativeElement);
  }

  public ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private isHostScrollable(): boolean {
    if (this.host?.nativeElement) {
      const style = window.getComputedStyle(this.host.nativeElement);
      return (
        style.getPropertyValue('overflow') === 'auto' ||
        style.getPropertyValue('overflow-y') === 'scroll'
      );
    }
    return false;
  }
}
